import { useState } from 'react';
import { Mail, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NewsletterCompact = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!firstName || !lastName || !email) {
      setStatus('error');
      setMessage('Please fill in all fields');
      return;
    }

    setStatus('loading');

    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, { 
        first_name: firstName,
        last_name: lastName,
        email 
      });
      setStatus('success');
      setMessage(response.data.message);
      setFirstName('');
      setLastName('');
      setEmail('');
    } catch (error) {
      setStatus('error');
      if (error.response?.data?.detail) {
        setMessage(error.response.data.detail);
      } else {
        setMessage('Something went wrong. Please try again.');
      }
    }
  };

  return (
    <section 
      data-testid="newsletter-compact"
      className="bg-[#002868] border-y border-white/10"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Left - Text */}
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex w-11 h-11 border border-white/25 items-center justify-center flex-shrink-0">
              <Mail className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-heading text-lg sm:text-lg text-white uppercase tracking-wide">
                Get the DC Sports Report Newsletter
              </h2>
              <p className="text-slate-400 text-sm hidden sm:block">
                Headlines, recaps, and podcast drops delivered to your inbox.
              </p>
            </div>
          </div>

          {/* Right - Form */}
          <form 
            onSubmit={handleSubmit} 
            data-testid="newsletter-compact-form"
            className="flex flex-col sm:flex-row gap-2 lg:gap-3"
          >
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                data-testid="newsletter-compact-firstname"
                className="h-10 w-28 bg-white border-0 rounded-none text-sm"
                disabled={status === 'loading'}
              />
              <Input
                type="text"
                placeholder="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                data-testid="newsletter-compact-lastname"
                className="h-10 w-28 bg-white border-0 rounded-none text-sm"
                disabled={status === 'loading'}
              />
            </div>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="newsletter-compact-email"
                className="h-10 flex-1 sm:w-48 bg-white border-0 rounded-none text-sm"
                disabled={status === 'loading'}
              />
              <Button
                type="submit"
                data-testid="newsletter-compact-submit"
                disabled={status === 'loading'}
                className="h-10 px-4 bg-[#BF0A30] hover:bg-[#9a0826] text-white font-bold text-sm uppercase rounded-none"
              >
                {status === 'loading' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Subscribe'
                )}
              </Button>
            </div>
          </form>
        </div>

        {/* Status Message */}
        {status !== 'idle' && status !== 'loading' && (
          <div 
            data-testid="newsletter-compact-status"
            className={`mt-3 flex items-center gap-2 text-sm font-medium ${
              status === 'success' ? 'text-green-400' : 'text-red-400'
            }`}
          >
            {status === 'success' ? (
              <CheckCircle className="w-4 h-4" />
            ) : (
              <AlertCircle className="w-4 h-4" />
            )}
            {message}
          </div>
        )}
      </div>
    </section>
  );
};

export default NewsletterCompact;
